import '@assets/fonts/fonts.css';
import {
  MuiTypography,
  MuiButton,
  MuiIconButton,
  MuiOutlinedInput,
  MuiTextField,
  MuiInputLabel,
  MuiFormLabel,
  MuiFormHelperText,
  MuiChip,
  MuiSelect,
  MuiAutocomplete,
  MuiBadge,
  MuiLink,
  MuiMenuItem,
  MuiToggleButtonGroup,
} from '@theme/styleOverrides';
import { scrollbarStyle } from '@theme/styles';
import colors from '@theme/colors';
import { ThemeMode } from '@theme/palettes/PaletteOptions.interface';
import { FontSize } from '@store/types/UserStoreSlice.type';
import { Palette } from '@mui/material';
import { adjustFontSize, adjustLineHeight, getTypographyVariants } from '@theme/styleOverrides/typography';

export const getComponents = (mode: ThemeMode, palette: Palette, fontSize: FontSize) => ({
  // FUTURE TODO: deprecate 'mode' and use palette to determine color
  // FUTURE TODO: pass in palette to anything that uses theme/colors inside and remove direct references to color
  MuiChip: MuiChip({ palette, fontSize }),
  MuiButton: MuiButton({ palette, fontSize }),
  MuiToggleButtonGroup: MuiToggleButtonGroup({ palette, fontSize }),
  MuiIconButton: MuiIconButton({ palette, fontSize }),
  MuiTypography: MuiTypography(palette),
  MuiOutlinedInput: MuiOutlinedInput({ fontSize }),
  MuiTextField,
  MuiInputLabel: MuiInputLabel({ fontSize }),
  MuiFormLabel: MuiFormLabel({ fontSize }),
  MuiFormHelperText: MuiFormHelperText({ fontSize }),
  MuiSelect: MuiSelect({ palette, fontSize }),
  MuiAutocomplete: MuiAutocomplete({ palette, fontSize }),
  MuiBadge: MuiBadge({ palette, fontSize }),
  MuiLink,
  MuiPopper: {
    styleOverrides: {
      root: {
        '.MuiAutocomplete-paper': {
          li: {
            ...getTypographyVariants(fontSize).bodySmall,
          },
        },
      },
    },
  },

  MuiMenuItem: MuiMenuItem({ palette, fontSize }),
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        '*': {
          ...scrollbarStyle,
        },
        root: {
          scrollbarGutter: 'stable',
        },
      },
    },
  },
  MuiInputAdornment: {
    styleOverrides: {
      root: {
        margin: '0px',
      },
      standard: {
        cursor: 'pointer',
      },
    },
  },
  MuiSvgIcon: {
    styleOverrides: {
      fontSizeSmall: {
        width: '20px',
        fontSize: `${adjustFontSize(14, fontSize)}px`,
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      elevation: {
        borderRadius: '8px',
        backgroundColor: palette.theme === 'lio' ? 'white' : palette.background.main,

        padding: 0,
      },
      outlined: {
        // backgroundColor: 'transparent',
        '& .MuiButton-text': {
          display: 'none',
        },
        '&:hover': {
          backgroundColor: palette.theme === 'lio' ? 'common.white' : palette.button.outlined,
          boxShadow: '0px 8px 20px -8px rgba(0, 0, 0, 0.25)',
          '& .MuiTypography-bodySmallBold': {
            color: palette.theme === 'lio' ? colors.SEAFOAM_SUPPLEMENTAL : colors.SUN_YELLOW_SUPPLEMENTAL,
          },
          '& .MuiButton-text': {
            display: 'flex',
          },
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        padding: '24px 16px',
        width: '100%',
        '&.MuiPaper-elevation1': {
          '&.MuiCard-root': {
            padding: '8px 8px 16px 8px',
          },
          '& .MuiCardHeader-root': {
            padding: 0,
          },
          '& .MuiCardContent-root': {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          },
          '& .MuiButton-startIcon': {
            marginLeft: 0,
          },
        },
        '&.MuiPaper-elevation2': {
          '&.MuiCard-root': {
            padding: '8px',
            // overflow: 'visible',
          },
          '& .MuiCardContent-root': {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          },
          '& .MuiButton-root': {
            padding: 0,
            minWidth: 'auto',
            borderRadius: '6px',
          },
          '& .MuiButton-startIcon': {
            marginLeft: 0,
          },
        },
        '&.MuiPaper-elevation3': {
          '&.MuiCard-root': {
            borderRadius: '4px',
            padding: '2px',
            // overflow: 'visible',
          },
        },
        '&.MuiPaper-elevation4': {
          '&.MuiCard-root': {
            borderRadius: '4px',
            padding: 0,
            border: 'none',
            // overflow: 'visible',
          },
          '& .MuiCardMedia-root': {
            borderRadius: '4px',
          },
        },
      },
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: 0,
        '&.MuiCardContent-root:last-child:not(.keep-padding)': {
          // TODO remove overly specific theme padding overrides as they are hard to override
          padding: 0,
        },
      },
    },
  },
  MuiCardActions: {
    styleOverrides: {
      root: {
        padding: 0,
      },
    },
  },
  MuiModal: {
    styleOverrides: {
      root: {
        padding: 0,
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        marginBottom: '16px',
        padding: 0,
        fontFamily: 'Sofia Pro',
        fontSize: `${adjustFontSize(20, fontSize)}px`,
        lineHeight: `${adjustLineHeight(24, fontSize)}px`,
        fontWeight: 400,
        letterSpacing: '0em',
        textAlign: 'center',
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: 0,
        marginBottom: '32px',
      },
    },
  },
  MuiDialogContentText: {
    styleOverrides: {
      root: {
        textAlign: 'center',
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      spacing: {
        padding: 0,
      },
      root: {
        justifyContent: 'center',
        '&.MuiDialogActions-root > :not(:first-of-type)': {
          marginLeft: '16px',
        },
      },
    },
  },
  MuiAvatar: {
    styleOverrides: {
      root: {
        fontFamily: 'Proxima Nova',
        fontWeight: 700,
        fontSize: `${adjustFontSize(16, fontSize)}px`,
        lineHeight: `${adjustLineHeight(24, fontSize)}px`,
        width: '32px',
        height: '32px',

        color: palette.common.white,
        backgroundColor: mode === 'light' ? colors.CORAL : '',
      },
      rounded: {
        width: '48px',
        height: '48px',
        borderRadius: '4px',
      },
    },
  },

  MuiDialog: {
    styleOverrides: {
      paper: {
        '&.MuiPaper-root': {
          boxShadow: '0px 8px 40px rgba(0, 0, 0, 0.2)',
        },
        '&.MuiPaper-elevation3': {
          '& .MuiDialogTitle-root': {
            boxShadow: '0px 2px 8px 1px rgba(0, 0, 0, 0.16)',
          },
          '& .MuiDialogActions-root': {
            boxShadow: '0px 2px 8px 1px rgba(0, 0, 0, 0.16)',
          },
        },
        '&.MuiPaper-elevation4': {
          '& .MuiDialogActions-root': {
            boxShadow: '0px 2px 8px 1px rgba(0, 0, 0, 0.16)',
          },
        },
      },
    },
  },
});
